import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { Cleaner, mapDeliveryInitialValues, mapDeliverySubmitValues, formatDeliveryValues } from '#component/shipment/form/helper';
import { processAfterSubmitAction, setDelivery, setFormStatus, setPristine } from '#action/shipment/form';
import { reduxFormResolver } from '#helper/rhf-resolver';
import { useStepSubmit } from '#component/shipment/form/use-step-submit';
import propTypes from './form/prop-types';
import validate from './form/validate';
import Form from './form';

const handleFormSubmit = (dispatch, measurementSystem, delivery) => async values => {
  const { states } = values;
  const newStates = formatDeliveryValues(states, measurementSystem, delivery);

  dispatch(setDelivery(Cleaner.cleanArray(mapDeliverySubmitValues(newStates))));
  await dispatch(processAfterSubmitAction());
  await dispatch(setFormStatus('succeeded'));
};

const DeliveryForm = props => {
  const { initialValues, onSubmit, editable, registerStepSubmit, form } = props;
  const dispatch = useDispatch();

  const methods = useForm({
    resolver: reduxFormResolver((values, context) => validate(values, { ...props, ...context })),
    defaultValues: initialValues || { states: [] },
    mode: 'onChange'
  });

  const { reset, formState: { isDirty } } = methods;
  const prevInitialRef = React.useRef(null);

  useEffect(() => {
    if (!initialValues) return;
    const serialized = JSON.stringify(initialValues);
    if (prevInitialRef.current === serialized) return;
    prevInitialRef.current = serialized;
    if (!isDirty) {
      reset(initialValues);
    }
  }, [initialValues, reset, isDirty]);

  useEffect(() => {
    if (form) {
      dispatch(setPristine(form, !isDirty));
    }
  }, [dispatch, form, isDirty]);

  const onFormSubmit = async values => {
    if (editable) {
      await onSubmit(values);
    } else {
      dispatch(processAfterSubmitAction());
    }
  };

  useStepSubmit(registerStepSubmit, methods, onFormSubmit);

  return (
    <FormProvider {...methods}>
      <Form {...props} onFormSubmit={onFormSubmit} />
    </FormProvider>
  );
};

function mapStateToProps(state, props) {
  const {
    shipment,
    delivery,
    account,
    shipmentForm
  } = state;
  const { dispatch, editable } = props;

  const currentStates = (shipmentForm.delivery?.length ?
    shipmentForm.delivery : shipment.states) ?? [];

  return {
    delivery,
    shipment,
    account,
    initialValues: { states: mapDeliveryInitialValues(currentStates, account.measurementSystem) },
    editable,
    onSubmit: handleFormSubmit(dispatch, account.measurementSystem, delivery)
  };
}

DeliveryForm.propTypes = propTypes;

export default connect(mapStateToProps)(DeliveryForm);
